import { Plus } from 'lucide-react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import { isItemActive, PRIMARY_NAV_ITEMS, type NavItem } from './nav-config';

import { GlassNav } from '@/shared/components/ui/glass-nav';
import { PulseIndicator } from '@/shared/components/ui/pulse-indicator';
import { cn } from '@/shared/lib/cn';

export interface BottomTabBarProps {
  testID?: string;
}

/**
 * Bottom tab nav (mobile <900). Layout fixo:
 *   [Home] [Grupos] [+FAB+] [Relatorios] [Perfil]
 * O FAB central abre `/transactions/new` (modal/sheet — Phase 06 implementa).
 */
export function BottomTabBar({ testID = 'app-bottom-tab' }: BottomTabBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [home, groups, reports, profile] = PRIMARY_NAV_ITEMS;
  if (!home || !groups || !reports || !profile) return null;

  return (
    <GlassNav
      as="nav"
      data-testid={testID}
      aria-label="Navegação primária mobile"
      className="sticky bottom-0 z-[var(--z-sticky)] flex items-end justify-between px-2 pb-[max(env(safe-area-inset-bottom),12px)] pt-3"
    >
      <TabItem item={home} active={isItemActive(location.pathname, home)} />
      <TabItem item={groups} active={isItemActive(location.pathname, groups)} />

      <button
        type="button"
        aria-label="Nova transação"
        data-testid="bottom-tab-fab"
        onClick={() =>
          navigate('/transactions/new', {
            state: {
              backgroundLocation: { pathname: location.pathname, search: location.search },
            },
          })
        }
        className={cn(
          'mx-1 -mt-6 inline-flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-pill bg-cta-gradient text-canvas shadow-fab transition-transform hover:scale-105 active:scale-95',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
        )}
      >
        <Plus size={26} aria-hidden />
      </button>

      <TabItem item={reports} active={isItemActive(location.pathname, reports)} />
      <TabItem item={profile} active={isItemActive(location.pathname, profile)} />
    </GlassNav>
  );
}

interface TabItemProps {
  item: NavItem;
  active: boolean;
}

function TabItem({ item, active }: TabItemProps) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      data-testid={item.testID}
      data-active={active ? 'true' : 'false'}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'relative flex h-12 w-16 flex-col items-center justify-center gap-0.5 rounded-base text-label-md transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
        active ? 'text-primary' : 'text-secondary-text',
      )}
    >
      <Icon size={22} aria-hidden />
      <span className="text-[10px] leading-none">{item.label}</span>
      {active ? (
        <span className="absolute -top-1 right-2" aria-hidden>
          <PulseIndicator />
        </span>
      ) : null}
    </NavLink>
  );
}
